import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Package, MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const STATUS_COLOR = {
    placed: 'bg-inorbit-yellow text-inorbit-blue',
    accepted: 'bg-inorbit-blue text-white',
    packed: 'bg-inorbit-blue text-white',
    ready: 'bg-inorbit-orange text-white',
    out_for_delivery: 'bg-inorbit-orange text-white',
    delivered: 'bg-emerald-600 text-white',
    cancelled: 'bg-rose-600 text-white',
};

export default function Orders() {
    const { type, loading } = useAuth();
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        if (type === 'customer') api.get('/orders/me').then(({ data }) => setOrders(data));
    }, [type]);

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/orders" replace />;

    return (
        <div className="pt-16 pb-32 min-h-screen" data-testid="orders-page">
            <section className="max-w-5xl mx-auto px-6 md:px-12 pt-12">
                <div className="overline mb-3">My Orders</div>
                <h1 className="font-serif text-5xl md:text-6xl text-inorbit-blue leading-tight">Your purchases</h1>
            </section>

            <section className="max-w-5xl mx-auto px-6 md:px-12 mt-10 space-y-5">
                {orders.length === 0 ? (
                    <div className="text-center py-20 text-text-tertiary">
                        <Package size={48} strokeWidth={1} className="mx-auto mb-4 text-inorbit-yellow" />
                        <div className="text-lg">No orders yet</div>
                        <Link to="/shop" className="btn-link mt-4 inline-flex">Start Shopping</Link>
                    </div>
                ) : orders.map((o) => (
                    <div key={o.id} className="card-luxury p-7" data-testid={`order-card-${o.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                            <div>
                                <div className="overline text-inorbit-orange">{o.brand_name}</div>
                                <div className="font-serif text-xl text-inorbit-blue mt-1">Order {o.order_number}</div>
                                <div className="text-xs text-text-tertiary mt-1">Placed {new Date(o.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                            </div>
                            <div className="text-right">
                                <span className={`pill text-xs ${STATUS_COLOR[o.status] || 'pill-blue'}`}>{o.status.replace(/_/g, ' ')}</span>
                                <div className="font-serif text-2xl text-inorbit-blue mt-2">₹{Math.round(o.total).toLocaleString('en-IN')}</div>
                            </div>
                        </div>
                        <div className="border-t border-border-subtle pt-4 space-y-2">
                            {o.items.map((it) => (
                                <div key={it.product_id} className="flex gap-3 text-sm">
                                    <img src={it.image} alt="" className="w-14 h-16 object-cover" />
                                    <div className="flex-1">
                                        <div className="text-inorbit-blue">{it.name}</div>
                                        <div className="text-xs text-text-tertiary">Qty {it.quantity} • ₹{it.price.toLocaleString('en-IN')}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle pt-3">
                            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-text-tertiary">
                                <span className="flex items-center gap-1"><MapPin size={11} /> {o.fulfilment === 'pickup' ? 'Mall Pickup' : `${o.address || ''}, ${o.city || ''}`}</span>
                                <span>+{o.points_earned || 0} points earned</span>
                                {o.payment_id && <span>Payment {o.payment_id}</span>}
                            </div>
                            <Link to={`/orders/${o.id}/track`} className="btn-outline border-inorbit-orange text-inorbit-orange hover:bg-inorbit-orange hover:text-white" data-testid={`track-order-${o.id}`}>
                                Track Order
                            </Link>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}
